import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
} from '@mui/material';
import { Grid } from '../../components/common/TypedGrid';
import { Add as AddIcon, LocationCity } from '@mui/icons-material';
import adminService, { City } from '../../services/adminService';
import { PageHeader, ResponsiveTable, StatCard, StatusChip } from '../../components/common';

const Cities: React.FC = () => {
  const [cities, setCities] = useState<City[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<City | null>(null);
  const [name, setName] = useState('');
  const [state, setState] = useState('');
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const loadCities = async () => {
    setLoading(true);
    try {
      setCities(await adminService.getAllCities());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadCities();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setState('');
    setDialogOpen(true);
  };

  const openEdit = (city: City) => {
    setEditing(city);
    setName(city.name);
    setState(city.state);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!name.trim() || !state.trim()) return;
    setSaving(true);
    try {
      if (editing) {
        await adminService.updateCity(editing.id, { name: name.trim(), state: state.trim() });
        setSnackbar({ message: 'City updated', severity: 'success' });
      } else {
        await adminService.createCity(name.trim(), state.trim());
        setSnackbar({ message: 'City created', severity: 'success' });
      }
      setDialogOpen(false);
      await loadCities();
    } catch (error: any) {
      setSnackbar({ message: error?.message || 'Failed to save city', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (city: City) => {
    try {
      await adminService.updateCity(city.id, { isActive: !city.isActive });
      await loadCities();
    } catch (error: any) {
      setSnackbar({ message: error?.message || 'Failed to update city', severity: 'error' });
    }
  };

  const activeCount = cities.filter((c) => c.isActive).length;

  return (
    <Box>
      <PageHeader
        title="Cities"
        subtitle="Every city the platform operates in — the boundary a city admin's access is scoped to"
        actions={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add City
          </Button>
        }
      />

      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4}>
          <StatCard label="Total Cities" value={cities.length} icon={<LocationCity />} tone="primary" />
        </Grid>
        <Grid item xs={6} sm={4}>
          <StatCard label="Active" value={activeCount} icon={<LocationCity />} tone="success" />
        </Grid>
      </Grid>

      <ResponsiveTable<City>
        rows={cities}
        rowKey={(c) => c.id}
        loading={loading}
        emptyTitle="No cities yet"
        emptyDescription="Add the first city to start assigning city admins and scoping ninjas to it."
        emptyAction={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            Add City
          </Button>
        }
        aria-label="Cities"
        columns={[
          { key: 'name', header: 'City', mobile: 'title', render: (c) => c.name },
          { key: 'state', header: 'State', mobile: 'subtitle', render: (c) => c.state },
          {
            key: 'status',
            header: 'Status',
            mobile: 'body',
            render: (c) => <StatusChip status={c.isActive ? 'active' : 'inactive'} />,
          },
          {
            key: 'actions',
            header: 'Actions',
            mobile: 'actions',
            render: (c) => (
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button size="small" onClick={() => openEdit(c)}>
                  Edit
                </Button>
                <Button size="small" color={c.isActive ? 'error' : 'success'} onClick={() => handleToggleActive(c)}>
                  {c.isActive ? 'Deactivate' : 'Activate'}
                </Button>
              </Box>
            ),
          },
        ]}
      />

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{editing ? 'Edit City' : 'Add City'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="City Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <TextField fullWidth label="State" value={state} onChange={(e) => setState(e.target.value)} />
          {editing && (
            <FormControlLabel
              sx={{ mt: 2 }}
              control={
                <Switch
                  checked={editing.isActive}
                  onChange={(e) => setEditing({ ...editing, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !name.trim() || !state.trim()}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? <Alert severity={snackbar.severity}>{snackbar.message}</Alert> : undefined}
      </Snackbar>
    </Box>
  );
};

export default Cities;
